import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Code,
  Trash2,
  FolderOpen,
  Folder,
  FolderPlus,
  Loader2,
  Pause,
  Sparkles,
  Github,
  Gitlab,
  ExternalLink,
  Search,
  X,
  MoreVertical,
  Copy,
  ChevronDown,
  Pencil,
  FolderInput,
  Bug,
  ArrowLeft,
} from "lucide-react";

import axios from "axios";
import toast from "react-hot-toast";

import { editorAPI } from "@/lib/editor/api";
import { TEMPLATE_ID } from "@/lib/templates";
import { useAuth } from "@/contexts/AuthContext";
import { useEmbedSubscriptionGate } from "@/embed/useEmbedSubscriptionGate";
import { EmbedSubscriptionBlockedModal } from "@/embed/EmbedSubscriptionBlockedModal";
import { usePermissions } from "@/hooks/usePermissions";
import { usePlatformAdmin } from "@/hooks/use-platform-admin";
import {
  buildCloneDeeplink,
  canBeCloneSource,
  getCurrentEnvironment,
} from "@/lib/clone/clone-config";
import { getActiveWorkspaceId } from "@/stores/workspace-store";
import { useProjectStore } from "@/stores/projectStore";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { usePageBar } from "@/components/layout/page-bar";
import { PauseContainerModal } from "@/components/dev-containers/PauseContainerModal";
import { GitHubImportModal } from "@/components/github";
import { GitLabImportModal } from "@/components/gitlab";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Project {
  id: string;
  name: string;
  description?: string;
  templateName?: string;
  createdAt: string;
  updatedAt: string;
  mode?: "lite" | "power";
  devContainerId?: string;
  workspaceId: string;
  thumbnailUrl?: string;
  latestPublishUrl?: string | null;
  folderId?: string | null;
}

interface ProjectFolderItem {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface DevContainerStatus {
  hasDevContainer: boolean;
  status: string | null;
  devContainerUrl: string | null;
  devContainerId?: string;
  subdomain?: string;
}

export default function EditorListFull() {
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  const { canCreateProject, canDeleteProject, canEditProject } =
    usePermissions();
  const { isPlatformAdmin } = usePlatformAdmin();
  const showCloneForDebugging = isPlatformAdmin && canBeCloneSource();
  const { projectListVersion } = useProjectStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [devContainerStatuses, setDevContainerStatuses] = useState<
    Record<string, DevContainerStatus>
  >({});
  const [lastPollTime, setLastPollTime] = useState<number | null>(null);
  const [pausingContainers, setPausingContainers] = useState<
    Record<string, boolean>
  >({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [creating, setCreating] = useState(false);
  const [generatingName, setGeneratingName] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseModalData, setPauseModalData] = useState<{
    devContainerId: string;
    taskCount: number;
    mode: "confirm" | "active-tasks" | "retry";
  } | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showGitlabImportModal, setShowGitlabImportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  // Folder state
  const [folders, setFolders] = useState<ProjectFolderItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentFolderName, setCurrentFolderName] = useState<string | null>(
    null,
  );
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [folderToRename, setFolderToRename] =
    useState<ProjectFolderItem | null>(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [folderToDelete, setFolderToDelete] =
    useState<ProjectFolderItem | null>(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [projectToMove, setProjectToMove] = useState<Project | null>(null);
  const [movingProject, setMovingProject] = useState(false);

  // Server-side search: projects already filtered by backend
  const projectsList = projects ?? [];
  const filteredProjects = projectsList;

  // Check if manual pause feature is enabled
  const enableManualPause =
    import.meta.env.VITE_ENABLE_MANUAL_CONTAINER_PAUSE === "true";

  // Auto-open import modal if redirected from GitHub OAuth
  useEffect(() => {
    if (searchParams.get("openImport") === "true") {
      setShowImportModal(true);
      // Clear the query param to avoid reopening on refresh
      searchParams.delete("openImport");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Auto-open GitLab import modal if redirected from GitLab OAuth
  useEffect(() => {
    if (searchParams.get("openGitlabImport") === "true") {
      setShowGitlabImportModal(true);
      searchParams.delete("openGitlabImport");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Auto-open create project modal when navigated from onboarding checklist
  useEffect(() => {
    if (searchParams.get("openCreateModal") === "true") {
      handleOpenCreateModal();
      searchParams.delete("openCreateModal");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (user) {
      loadProjects();
      loadFolders();
    }
  }, [user]);

  // Refresh project list when projectListVersion changes (e.g., after rename)
  useEffect(() => {
    // Skip initial mount (version 0), only refetch on increments
    if (projectListVersion > 0 && user) {
      loadProjects();
    }
  }, [projectListVersion]);

  // Reload projects when folder changes
  useEffect(() => {
    if (user) {
      setSearchQuery("");
      setDebouncedSearch("");
      setLoading(true);
      loadProjects();
    }
  }, [currentFolderId]);

  // Delta sync polling - single call for all projects instead of N calls
  useEffect(() => {
    // Only poll if there are Power mode projects
    const hasPowerProjects = projects.some(
      (p) => p.mode === "power" && p.devContainerId,
    );
    if (!hasPowerProjects) return;

    const pollInterval = setInterval(async () => {
      await pollDevContainerStatusChanges();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [projects, lastPollTime]);

  // Debounce search query (300ms)
  // Show spinner immediately when user types, hide when API completes
  useEffect(() => {
    const trimmed = searchQuery.trim();

    // Show spinner immediately when user starts typing
    if (trimmed) {
      setSearchLoading(true);
    }

    const timeoutId = setTimeout(() => {
      setDebouncedSearch(trimmed);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Trigger API search when debounced search changes
  useEffect(() => {
    if (!user) return;
    // Skip initial load (handled by user useEffect)
    if (debouncedSearch === "" && loading) return;
    loadProjects(debouncedSearch || undefined);
  }, [debouncedSearch]);

  const loadFolders = async () => {
    try {
      const data = await editorAPI.listFolders();
      setFolders(data);
    } catch (error) {
      console.error("Failed to load folders:", error);
    }
  };

  const loadProjects = async (search?: string) => {
    try {
      // Always show loading when actively searching (search param provided)
      if (search !== undefined) {
        setSearchLoading(true);
      }
      const filters: { search?: string; folderId?: string | null } = {};
      if (search) {
        filters.search = search;
        // When searching inside a folder, scope to that folder
        if (currentFolderId) {
          filters.folderId = currentFolderId;
        }
      } else {
        // When not searching, filter by current folder (null = root)
        filters.folderId = currentFolderId;
      }
      const data = await editorAPI.listProjects(filters);
      setProjects(data);

      // Fetch initial statuses for all Power mode projects (no since param = get all)
      const hasPowerProjects = data.some(
        (p: Project) => p.mode === "power" && p.devContainerId,
      );
      if (hasPowerProjects) {
        // Pass data directly since setProjects is async and state not updated yet
        await pollDevContainerStatusChanges(true, data);
      }
    } catch (error) {
      console.error("Failed to load projects:", error);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  /**
   * Poll for dev container status changes using delta sync
   * @param initial - If true, fetches all statuses (no since param)
   * @param projectList - Optional projects list (used on initial load when state not yet updated)
   */
  const pollDevContainerStatusChanges = async (
    initial = false,
    projectList?: Project[],
  ) => {
    try {
      const since = initial ? undefined : (lastPollTime ?? undefined);
      const { changes, serverTime } =
        await editorAPI.getDevContainerStatusChanges(since);

      // Update serverTime for next poll
      setLastPollTime(serverTime);

      // Use provided list or current state
      const projectsToMatch = projectList || projects;

      // Apply changes to state (idempotent - duplicates just overwrite with same value)
      // Match by devContainerId since frontend projects already have this field
      if (changes.length > 0) {
        setDevContainerStatuses((prev) => {
          const updated = { ...prev };
          for (const change of changes) {
            // Find project by devContainerId
            const project = projectsToMatch.find(
              (p) => p.devContainerId === change.devContainerId,
            );
            if (project) {
              // Use response directly - matches individual API shape
              updated[project.id] = change;
            }
          }
          return updated;
        });
      }
    } catch (error) {
      console.error("Failed to poll dev container status changes:", error);
    }
  };

  // Keep legacy function for pause action refresh (single project)
  const checkDevContainerStatus = async (projectId: string) => {
    try {
      const status = await editorAPI.getProjectDevContainerStatus(projectId);
      setDevContainerStatuses((prev) => ({ ...prev, [projectId]: status }));
    } catch (error) {
      console.error("Failed to check dev container status:", error);
    }
  };

  const handleDeleteClick = (projectId: string, projectName: string) => {
    setProjectToDelete({ id: projectId, name: projectName });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;

    setDeleting(true);
    try {
      await editorAPI.deleteProject(projectToDelete.id);
      setShowDeleteModal(false);
      setProjectToDelete(null);
      await loadProjects();
    } catch (error) {
      console.error("Failed to delete project:", error);
      toast.error("Failed to delete project. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // Folder handlers
  const handleOpenFolder = (folder: ProjectFolderItem) => {
    setCurrentFolderId(folder.id);
    setCurrentFolderName(folder.name);
  };

  const handleBackToRoot = () => {
    setCurrentFolderId(null);
    setCurrentFolderName(null);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      await editorAPI.createFolder(newFolderName.trim());
      setShowCreateFolderModal(false);
      setNewFolderName("");
      await loadFolders();
    } catch (error: any) {
      console.error("Failed to create folder:", error);
      toast.error(error.response?.data?.message || "Failed to create folder");
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRenameFolder = async () => {
    if (!folderToRename || !renameFolderName.trim()) return;
    try {
      await editorAPI.renameFolder(folderToRename.id, renameFolderName.trim());
      setShowRenameFolderModal(false);
      setFolderToRename(null);
      await loadFolders();
      // Update current folder name if we renamed the folder we're in
      if (currentFolderId === folderToRename.id) {
        setCurrentFolderName(renameFolderName.trim());
      }
    } catch (error: any) {
      console.error("Failed to rename folder:", error);
      toast.error(error.response?.data?.message || "Failed to rename folder");
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    setDeletingFolder(true);
    try {
      await editorAPI.deleteFolder(folderToDelete.id);
      setShowDeleteFolderModal(false);
      setFolderToDelete(null);
      await loadFolders();
      // If we deleted the folder we were in, go back to root
      if (currentFolderId === folderToDelete.id) {
        handleBackToRoot();
      }
    } catch (error: any) {
      console.error("Failed to delete folder:", error);
      const message =
        error.response?.data?.message || "Failed to delete folder";
      toast.error(message);
    } finally {
      setDeletingFolder(false);
    }
  };

  const handleMoveProject = async (targetFolderId: string | null) => {
    if (!projectToMove) return;
    setMovingProject(true);
    try {
      await editorAPI.moveProjectToFolder(projectToMove.id, targetFolderId);
      setShowMoveModal(false);
      setProjectToMove(null);
      await loadProjects();
      toast.success("Project moved");
    } catch (error) {
      console.error("Failed to move project:", error);
      toast.error("Failed to move project");
    } finally {
      setMovingProject(false);
    }
  };

  // Handler for pause button click - ALWAYS shows confirmation modal
  const handlePauseClick = async (devContainerId: string) => {
    if (!devContainerId) return;

    try {
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required. Please log in again.");
        return;
      }

      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
      // Check for active tasks to determine modal content
      const { data } = await axios.get(
        `${API_URL}/api/claude-agent/tasks/active/dev-container/${devContainerId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Always show modal - mode determines content
      const mode = data.hasActiveTasks ? "active-tasks" : "confirm";
      setPauseModalData({ devContainerId, taskCount: data.count, mode });
      setShowPauseModal(true);
    } catch (error) {
      console.error("Failed to check active tasks:", error);
      // Fallback: show simple confirmation modal
      setPauseModalData({ devContainerId, taskCount: 0, mode: "confirm" });
      setShowPauseModal(true);
    }
  };

  // Actual pause handler with force option
  const handlePauseContainer = async (
    devContainerId: string,
    force: boolean = false,
  ) => {
    if (!devContainerId) return;

    setPausingContainers((prev) => ({ ...prev, [devContainerId]: true }));

    try {
      const token = await getIdToken();
      if (!token) {
        console.error("No authentication token");
        toast.error("Authentication required. Please log in again.");
        return;
      }

      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
      await axios.post(
        `${API_URL}/api/dev-containers/workspaces/${devContainerId}/pause${force ? "?force=true" : ""}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Reload project status after pausing
      await checkDevContainerStatus(
        projects.find((p) => p.devContainerId === devContainerId)?.id || "",
      );
      toast.success("Container paused successfully");
      setShowPauseModal(false);
      setPauseModalData(null);
    } catch (error: any) {
      console.error("Failed to pause container:", error);
      const message =
        error.response?.data?.message ||
        "Failed to pause container. Please try again.";

      // Check if it's a backup failure - show retry modal instead of toast
      if (message.includes("backup failed") && !force) {
        // Show retry modal for trying again with force
        setPauseModalData({ devContainerId, taskCount: 0, mode: "retry" });
        // Keep modal open, don't show toast
        return;
      }

      toast.error(message);
      setShowPauseModal(false);
      setPauseModalData(null);
    } finally {
      setPausingContainers((prev) => ({ ...prev, [devContainerId]: false }));
    }
  };

  // Modal handler for pause confirmation
  const handlePauseConfirm = async (force: boolean) => {
    if (pauseModalData) {
      await handlePauseContainer(pauseModalData.devContainerId, force);
    }
  };

  const handleOpenCreateModal = async () => {
    setProjectName("");
    setShowCreateModal(true);
    await generateProjectName();
  };

  const generateProjectName = async () => {
    try {
      setGeneratingName(true);
      const response = await editorAPI.suggestProjectName();
      setProjectName(response.suggestedName);
    } catch (error) {
      console.error("Failed to generate project name:", error);
    } finally {
      setGeneratingName(false);
    }
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) return;

    setCreating(true);
    try {
      const project = await editorAPI.cloneTemplate(TEMPLATE_ID.BASE, {
        projectName,
        mode: "power",
        folderId: currentFolderId || undefined,
      });

      // Navigate to the newly created project
      navigate(`/dashboard/editor/${project.id}`);
      setShowCreateModal(false);
    } catch (error: any) {
      console.error("Failed to create project:", error);
      // Show specific error message from backend (e.g., slug collision)
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create project. Please try again.";
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const getFolderName = (
    folderId: string | null | undefined,
  ): string | null => {
    if (!folderId) return null;
    return folders.find((f) => f.id === folderId)?.name || null;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderProjectCardGrid = (project: Project): JSX.Element => {
    const status = devContainerStatuses[project.id];
    const isProvisioning =
      project.mode === "power" &&
      status?.status &&
      !["running", "stopped", "paused", "terminated", "error"].includes(
        status.status,
      );
    const isReady =
      project.mode === "power" &&
      (status?.status === "running" || status?.status === "paused");
    const hasError = project.mode === "power" && status?.status === "error";

    const openTo = `/dashboard/editor/${project.id}${project.mode === "power" ? "/power" : ""}?ws=${project.workspaceId}`;
    const folderNameWhenSearching =
      debouncedSearch && !currentFolderId
        ? getFolderName(project.folderId)
        : undefined;

    return (
      <div
        key={project.id}
        className="group relative flex flex-col bg-card rounded-xl border border-border hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
      >
        {/* Stretched link makes the whole card clickable (skipped while provisioning) */}
        {!isProvisioning && (
          <Link
            to={openTo}
            aria-label={`Open ${project.name}`}
            className="absolute inset-0 z-[1] rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        )}

        {/* Thumbnail preview or placeholder */}
        <div className="relative aspect-[16/10] bg-accent dark:bg-white/[0.04] overflow-hidden">
          {project.thumbnailUrl ? (
            <img
              src={project.thumbnailUrl}
              alt={`${project.name} preview`}
              loading="lazy"
              className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          <div
            className={`absolute inset-0 flex items-center justify-center ${project.thumbnailUrl ? "hidden" : ""}`}
          >
            <Code className="w-6 h-6 text-gray-400 dark:text-gray-600" />
          </div>

          {/* Status pill (top-left) */}
          {project.mode === "power" &&
            (isProvisioning || isReady || hasError) && (
              <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 px-2 py-1 rounded-md bg-background/80 backdrop-blur-sm border border-border/50">
                <span
                  className={`inline-flex rounded-full h-1.5 w-1.5 ${
                    isProvisioning
                      ? "bg-amber-500 animate-pulse"
                      : hasError
                        ? "bg-red-500"
                        : "bg-emerald-500"
                  }`}
                />
                <span className="text-[10px] font-medium text-foreground/90">
                  {isProvisioning
                    ? "Setting up"
                    : hasError
                      ? "Error"
                      : "Ready"}
                </span>
              </div>
            )}

          {/* Secondary actions (top-right) */}
          <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
            {project.mode === "power" &&
              status?.devContainerUrl &&
              enableManualPause &&
              canEditProject &&
              project.devContainerId && (
                <button
                  onClick={() => handlePauseClick(project.devContainerId!)}
                  disabled={
                    pausingContainers[project.devContainerId!] ||
                    status?.status === "paused"
                  }
                  className="flex h-7 w-7 items-center justify-center bg-background/90 backdrop-blur-sm border border-border text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                  title={
                    status?.status === "paused"
                      ? "Container is paused"
                      : "Pause container"
                  }
                >
                  {pausingContainers[project.devContainerId!] ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Pause size={14} />
                  )}
                </button>
              )}
            {project.latestPublishUrl && (
              <a
                href={project.latestPublishUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-7 w-7 items-center justify-center bg-background/90 backdrop-blur-sm border border-border text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                title="Open published site"
              >
                <ExternalLink size={14} />
              </a>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-7 w-7 items-center justify-center bg-background/90 backdrop-blur-sm border border-border text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors focus:outline-none focus-visible:ring-0"
                  title="More options"
                >
                  <MoreVertical size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {project.latestPublishUrl && (
                  <DropdownMenuItem
                    onClick={() => {
                      navigator.clipboard.writeText(project.latestPublishUrl!);
                      toast.success("Link copied");
                    }}
                  >
                    <Copy size={14} className="mr-2" />
                    Copy link
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => {
                    setProjectToMove(project);
                    setShowMoveModal(true);
                  }}
                >
                  <FolderInput size={14} className="mr-2" />
                  Move to folder
                </DropdownMenuItem>
                {showCloneForDebugging && project.mode === "power" && (
                  <DropdownMenuItem
                    onClick={() => {
                      const env = getCurrentEnvironment();
                      if (env !== "sandbox2" && env !== "production") return;
                      const url = buildCloneDeeplink({
                        sourceEnv: env,
                        sourceProjectId: project.id,
                      });
                      console.log("[clone] opening deeplink", {
                        sourceEnv: env,
                        sourceProjectId: project.id,
                        url,
                      });
                      window.open(url, "_blank", "noopener");
                    }}
                  >
                    <Bug size={14} className="mr-2" />
                    Clone for debugging (sandbox1)
                  </DropdownMenuItem>
                )}
                {canDeleteProject && (
                  <DropdownMenuItem
                    onClick={() => handleDeleteClick(project.id, project.name)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 size={14} className="mr-2" />
                    Delete project
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="p-3 flex flex-col gap-1.5">
          <h3 className="font-semibold text-sm text-foreground truncate">
            {project.name}
          </h3>

          {project.description && (
            <p className="text-xs text-muted-foreground line-clamp-2">
              {project.description}
            </p>
          )}

          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
            <span>{formatDate(project.updatedAt)}</span>
            {folderNameWhenSearching && (
              <span className="flex items-center gap-1 truncate">
                <span className="text-border">·</span>
                <Folder className="h-3 w-3 shrink-0" />
                <span className="truncate">{folderNameWhenSearching}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const { isBlocked: isEmbedBlocked } = useEmbedSubscriptionGate();

  // Shell page bar: title/crumb/count left, search + folder/project actions right
  usePageBar({
    title: "Projects",
    description: "Build, preview, and ship your apps and dashboards.",
    meta: loading
      ? undefined
      : `${filteredProjects.length} project${filteredProjects.length === 1 ? "" : "s"}`,
    actions: (
      <>
        {(projectsList.length > 0 || folders.length > 0 || debouncedSearch) && (
          <div className="relative w-full min-w-[6rem] flex-1 sm:w-52 sm:flex-none">
            {searchLoading ? (
              <div className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2">
                <svg
                  className="h-4 w-4 animate-spin text-muted-foreground"
                  style={{ transformOrigin: "center" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="47 16"
                  />
                </svg>
              </div>
            ) : (
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            )}
            <Input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-8 text-sm"
            />
            {searchQuery.trim() && !searchLoading && (
              <button
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        {canCreateProject && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                className="h-9 flex-shrink-0 bg-foreground text-background hover:bg-foreground/90"
              >
                New Project
                <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={handleOpenCreateModal}>
                <Plus className="h-4 w-4 mr-2" />
                Blank Project
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowImportModal(true)}>
                <Github className="h-4 w-4 mr-2" />
                Import from GitHub
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowGitlabImportModal(true)}>
                <Gitlab className="h-4 w-4 mr-2" />
                Import from GitLab
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </>
    ),
  });

  if (isEmbedBlocked) {
    return <EmbedSubscriptionBlockedModal />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="px-6 sm:px-8 lg:px-10 pt-6 pb-10">
        {/* Folder breadcrumb — a folder is a filter within Projects, not a page,
            so it lives in the content, not the shell page header. */}
        {currentFolderId && (
          <button
            onClick={handleBackToRoot}
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>All projects</span>
            <span className="text-muted-foreground/40">/</span>
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <FolderOpen className="h-4 w-4" />
              {currentFolderName}
            </span>
          </button>
        )}
        {/* Folders grid - always at root level when not searching; "New Folder" lives here */}
        {!currentFolderId &&
          !debouncedSearch &&
          (folders.length > 0 || canCreateProject) && (
          <div className="mb-8">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 px-1">
              Folders
            </h3>
            {/* Mobile folders — horizontally scrollable pills (matches desktop chips) */}
            <div className="sm:hidden -mx-6 flex gap-2 overflow-x-auto px-6 pb-1">
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className="group flex h-10 flex-none items-center gap-1.5 rounded-lg border border-border bg-card pl-3 pr-1 transition-colors cursor-pointer active:bg-muted"
                  onClick={() => handleOpenFolder(folder)}
                >
                  <Folder size={16} className="text-muted-foreground shrink-0" />
                  <span className="max-w-[150px] truncate whitespace-nowrap text-sm font-medium text-foreground">
                    {folder.name}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="p-1.5 text-muted-foreground rounded-md shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical size={14} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderToRename(folder);
                          setRenameFolderName(folder.name);
                          setShowRenameFolderModal(true);
                        }}
                      >
                        <Pencil size={14} className="mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderToDelete(folder);
                          setShowDeleteFolderModal(true);
                        }}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Delete folder
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
              {canCreateProject && (
                <button
                  onClick={() => {
                    setNewFolderName("");
                    setShowCreateFolderModal(true);
                  }}
                  className="flex h-10 flex-none items-center gap-1.5 whitespace-nowrap rounded-lg border border-dashed border-primary/40 bg-card px-3 text-sm font-medium text-foreground/80 active:bg-muted"
                >
                  <FolderPlus size={15} className="text-primary" />
                  New Folder
                </button>
              )}
            </div>

            {/* Desktop folders */}
            <div className="hidden sm:flex sm:flex-wrap gap-2">
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className="group flex items-center gap-2 h-10 pl-1.5 pr-1.5 bg-card rounded-lg border border-border hover:border-foreground/20 hover:shadow-sm transition-all duration-200 cursor-pointer"
                  onClick={() => handleOpenFolder(folder)}
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted shrink-0 group-hover:bg-accent transition-colors">
                    <Folder size={15} className="text-muted-foreground" />
                  </span>
                  <span className="font-medium text-sm text-foreground truncate max-w-[160px]">
                    {folder.name}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="p-1 text-muted-foreground hover:bg-muted/80 rounded-md transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical size={12} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderToRename(folder);
                          setRenameFolderName(folder.name);
                          setShowRenameFolderModal(true);
                        }}
                      >
                        <Pencil size={14} className="mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderToDelete(folder);
                          setShowDeleteFolderModal(true);
                        }}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Delete folder
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
              {canCreateProject && (
                <button
                  onClick={() => {
                    setNewFolderName("");
                    setShowCreateFolderModal(true);
                  }}
                  className="flex items-center gap-2 h-10 px-3 rounded-lg border border-dashed border-primary/40 bg-card text-sm font-medium text-foreground/80 hover:text-foreground hover:border-primary/70 hover:shadow-sm transition-all duration-200"
                >
                  <FolderPlus size={15} className="text-primary" />
                  New Folder
                </button>
              )}
            </div>
          </div>
        )}

        {projectsList.length === 0 && debouncedSearch ? (
          // No search results
          <div className="text-center py-8 sm:py-12 bg-card rounded-2xl sm:rounded-lg border border-border">
            <Search
              size={40}
              className="sm:hidden mx-auto text-muted-foreground mb-3"
            />
            <Search
              size={48}
              className="hidden sm:block mx-auto text-muted-foreground mb-4"
            />
            <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">
              No projects found
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mb-4 px-4">
              No projects match "{searchQuery.trim()}"
            </p>
            <button
              onClick={() => setSearchQuery("")}
              className="px-5 py-2.5 sm:px-4 sm:py-2 border border-border hover:bg-muted text-foreground rounded-xl sm:rounded-lg transition-colors text-sm"
            >
              Clear search
            </button>
          </div>
        ) : projectsList.length === 0 &&
          !currentFolderId &&
          folders.length === 0 ? (
          // No projects and no folders at all (empty state)
          <div className="text-center py-8 sm:py-12 bg-card rounded-2xl sm:rounded-lg border border-border">
            <FolderOpen
              size={40}
              className="sm:hidden mx-auto text-muted-foreground mb-3"
            />
            <FolderOpen
              size={48}
              className="hidden sm:block mx-auto text-muted-foreground mb-4"
            />
            <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">
              No projects yet
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mb-4 px-4">
              {canCreateProject
                ? "Create your first project to get started"
                : "No projects available"}
            </p>
            {canCreateProject && (
              <Button
                size="sm"
                onClick={handleOpenCreateModal}
                className="h-9 bg-foreground text-background hover:bg-foreground/90"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Create Project
              </Button>
            )}
          </div>
        ) : projectsList.length === 0 &&
          (currentFolderId || folders.length === 0) ? (
          // Empty folder, or root with no folders
          <div className="text-center py-8 sm:py-12 bg-card rounded-2xl sm:rounded-lg border border-border">
            <FolderOpen
              size={40}
              className="sm:hidden mx-auto text-muted-foreground mb-3"
            />
            <FolderOpen
              size={48}
              className="hidden sm:block mx-auto text-muted-foreground mb-4"
            />
            <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">
              {currentFolderId ? "This folder is empty" : "No projects yet"}
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mb-4 px-4">
              {canCreateProject
                ? currentFolderId
                  ? "Move projects here or create a new one"
                  : "Create your first project to get started"
                : "No projects available"}
            </p>
            {canCreateProject && (
              <Button
                size="sm"
                onClick={handleOpenCreateModal}
                className="h-9 bg-foreground text-background hover:bg-foreground/90"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Create Project
              </Button>
            )}
          </div>
        ) : (
          <div
            className={
              !currentFolderId &&
              !debouncedSearch &&
              (folders.length > 0 || canCreateProject)
                ? "rounded-xl bg-white dark:bg-card border border-border/50 p-4 sm:p-5"
                : ""
            }
          >
            {!currentFolderId &&
              !debouncedSearch &&
              (folders.length > 0 || canCreateProject) && (
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4 px-1">
                All Projects
              </h3>
            )}
            {/* Root with folders but no projects yet — prompt inside the section */}
            {filteredProjects.length === 0 && canCreateProject && (
              <div className="flex flex-col items-center gap-3 py-10 sm:py-14">
                <p className="text-sm text-muted-foreground">
                  No projects yet
                </p>
                <Button
                  size="sm"
                  onClick={handleOpenCreateModal}
                  className="h-9 bg-foreground text-background hover:bg-foreground/90"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Create Project
                </Button>
              </div>
            )}
            {/* Mobile: Clean list view */}
            <div className="sm:hidden flex flex-col gap-3">
              {filteredProjects.map((project) => {
                const status = devContainerStatuses[project.id];
                const isProvisioning =
                  project.mode === "power" &&
                  status?.status &&
                  ![
                    "running",
                    "stopped",
                    "paused",
                    "terminated",
                    "error",
                  ].includes(status.status);
                const isReady =
                  project.mode === "power" &&
                  (status?.status === "running" || status?.status === "paused");
                const hasError =
                  project.mode === "power" && status?.status === "error";

                const openTo = `/dashboard/editor/${project.id}${project.mode === "power" ? "/power" : ""}?ws=${project.workspaceId}`;

                return (
                  <div
                    key={project.id}
                    className="group relative bg-card rounded-2xl border border-border hover:border-purple-500/50 transition-all duration-200 overflow-hidden"
                  >
                    {/* Whole card is clickable — tap anywhere to open
                        (skipped while provisioning). */}
                    {!isProvisioning && (
                      <Link
                        to={openTo}
                        aria-label={`Open ${project.name}`}
                        className="absolute inset-0 z-[1] rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      />
                    )}

                    {/* More options - top right */}
                    <div className="absolute top-2 right-2 z-10">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="flex p-1.5 items-center justify-center text-muted-foreground hover:bg-muted/80 bg-card/80 backdrop-blur-sm rounded-lg transition-colors"
                            title="More options"
                          >
                            <MoreVertical size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {project.latestPublishUrl && (
                            <DropdownMenuItem
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  project.latestPublishUrl!,
                                );
                                toast.success("Link copied");
                              }}
                            >
                              <Copy size={14} className="mr-2" />
                              Copy link
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => {
                              setProjectToMove(project);
                              setShowMoveModal(true);
                            }}
                          >
                            <FolderInput size={14} className="mr-2" />
                            Move to folder
                          </DropdownMenuItem>
                          {showCloneForDebugging &&
                            project.mode === "power" && (
                              <DropdownMenuItem
                                onClick={() => {
                                  const env = getCurrentEnvironment();
                                  if (
                                    env !== "sandbox2" &&
                                    env !== "production"
                                  )
                                    return;
                                  const url = buildCloneDeeplink({
                                    sourceEnv: env,
                                    sourceProjectId: project.id,
                                  });
                                  console.log("[clone] opening deeplink", {
                                    sourceEnv: env,
                                    sourceProjectId: project.id,
                                    url,
                                  });
                                  window.open(url, "_blank", "noopener");
                                }}
                              >
                                <Bug size={14} className="mr-2" />
                                Clone for debugging (sandbox1)
                              </DropdownMenuItem>
                            )}
                          {canDeleteProject && (
                            <DropdownMenuItem
                              onClick={() =>
                                handleDeleteClick(project.id, project.name)
                              }
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 size={14} className="mr-2" />
                              Delete project
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="flex items-center gap-3 p-3 overflow-hidden">
                      {/* Thumbnail - square on left */}
                      <div className="relative w-20 h-20 flex-shrink-0 bg-accent dark:bg-white/[0.04] rounded-xl overflow-hidden">
                        {project.thumbnailUrl ? (
                          <img
                            src={project.thumbnailUrl}
                            alt={`${project.name} preview`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.nextElementSibling?.classList.remove(
                                "hidden",
                              );
                            }}
                          />
                        ) : null}
                        <div
                          className={`absolute inset-0 flex items-center justify-center ${project.thumbnailUrl ? "hidden" : ""}`}
                        >
                          <Code className="w-6 h-6 text-gray-400 dark:text-gray-600" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pr-6">
                        <h3 className="font-semibold text-base text-foreground truncate">
                          {project.name}
                        </h3>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span>{formatDate(project.updatedAt)}</span>
                          {debouncedSearch &&
                            !currentFolderId &&
                            getFolderName(project.folderId) && (
                              <>
                                <span>·</span>
                                <Folder className="h-3 w-3 shrink-0" />
                                <span className="truncate max-w-[120px]">
                                  {getFolderName(project.folderId)}
                                </span>
                              </>
                            )}
                        </div>
                        {project.mode === "power" && isProvisioning && (
                          <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-500">
                            <Loader2 size={12} className="animate-spin" />
                            Setting up…
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status dot — bottom-right of the whole card */}
                    {project.mode === "power" &&
                      (isProvisioning || isReady || hasError) && (
                        <div className="absolute bottom-1 right-2 z-10">
                          <span
                            className={`inline-flex h-2.5 w-2.5 rounded-full ring-2 ring-card ${
                              isProvisioning
                                ? "bg-amber-500"
                                : isReady
                                  ? "bg-emerald-500"
                                  : "bg-red-500"
                            }`}
                          ></span>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>

            {/* Desktop: Grid view */}
            <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredProjects.map(renderProjectCardGrid)}
            </div>
          </div>
        )}

        {/* Create Project Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create New Project"
          size="md"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="project-name">Project Name *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="project-name"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && projectName.trim() && !creating && !generatingName) {
                      e.preventDefault();
                      handleCreateProject();
                    }
                  }}
                  placeholder="My Awesome Project"
                  disabled={generatingName}
                  className="flex-1"
                  autoFocus
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={generateProjectName}
                  disabled={generatingName}
                  className="h-10 px-3"
                >
                  {generatingName ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex gap-2 pt-2">

              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateProject}
                disabled={!projectName.trim() || creating}
                className="flex-1"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Project"
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setProjectToDelete(null);
          }}
          title="Delete Project"
          size="md"
        >
          <div className="space-y-4">
            <p className="text-foreground">
              Are you sure you want to delete{" "}
              <strong>"{projectToDelete?.name}"</strong>?
            </p>
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. All project files and data will be
              permanently deleted.
            </p>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setProjectToDelete(null);
                }}
                disabled={deleting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete Project"
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Pause Confirmation Modal */}
        <PauseContainerModal
          isOpen={showPauseModal}
          onClose={() => {
            setShowPauseModal(false);
            setPauseModalData(null);
          }}
          onConfirm={handlePauseConfirm}
          taskCount={pauseModalData?.taskCount ?? 0}
          isPausing={
            pauseModalData
              ? (pausingContainers[pauseModalData.devContainerId] ?? false)
              : false
          }
          mode={pauseModalData?.mode ?? "confirm"}
        />

        {/* GitHub Import Modal */}
        <GitHubImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={(projectId) => {
            setShowImportModal(false);
            loadProjects();
            // Navigate to the imported project (Power Mode) with workspace context
            const wsId = getActiveWorkspaceId();
            navigate(
              `/dashboard/editor/${projectId}/power${wsId ? `?ws=${wsId}` : ""}`,
            );
          }}
        />

        {/* GitLab Import Modal */}
        <GitLabImportModal
          isOpen={showGitlabImportModal}
          onClose={() => setShowGitlabImportModal(false)}
          onSuccess={(projectId) => {
            setShowGitlabImportModal(false);
            loadProjects();
            const wsId = getActiveWorkspaceId();
            navigate(
              `/dashboard/editor/${projectId}/power${wsId ? `?ws=${wsId}` : ""}`,
            );
          }}
        />

        {/* Create Folder Modal */}
        <Modal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          title="New Folder"
          size="md"
        >
          <div className="space-y-4">
            <div>
              <Input
                id="folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFolderName.trim()) {
                    handleCreateFolder();
                  }
                }}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateFolderModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || creatingFolder}
                className="flex-1"
              >
                {creatingFolder ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Folder"
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Rename Folder Modal */}
        <Modal
          isOpen={showRenameFolderModal}
          onClose={() => {
            setShowRenameFolderModal(false);
            setFolderToRename(null);
          }}
          title="Rename Folder"
          size="md"
        >
          <div className="space-y-4">
            <div>
              <Input
                id="rename-folder-name"
                value={renameFolderName}
                onChange={(e) => setRenameFolderName(e.target.value)}
                placeholder="Folder name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && renameFolderName.trim()) {
                    handleRenameFolder();
                  }
                }}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRenameFolderModal(false);
                  setFolderToRename(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRenameFolder}
                disabled={!renameFolderName.trim()}
                className="flex-1"
              >
                Rename
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Folder Modal */}
        <Modal
          isOpen={showDeleteFolderModal}
          onClose={() => {
            setShowDeleteFolderModal(false);
            setFolderToDelete(null);
          }}
          title="Delete Folder"
          size="md"
        >
          <div className="space-y-4">
            <p className="text-foreground">
              Are you sure you want to delete the folder{" "}
              <strong>"{folderToDelete?.name}"</strong>?
            </p>
            <p className="text-sm text-muted-foreground">
              The folder must be empty. Move or delete all projects inside
              first.
            </p>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteFolderModal(false);
                  setFolderToDelete(null);
                }}
                disabled={deletingFolder}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteFolder}
                disabled={deletingFolder}
                className="flex-1"
              >
                {deletingFolder ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete Folder"
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Move to Folder Modal */}
        <Modal
          isOpen={showMoveModal}
          onClose={() => {
            setShowMoveModal(false);
            setProjectToMove(null);
          }}
          title={`Move "${projectToMove?.name || ""}"`}
          size="md"
        >
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground mb-4">
              Select a destination:
            </p>

            {/* Root option */}
            <button
              onClick={() => handleMoveProject(null)}
              disabled={
                movingProject ||
                projectToMove?.folderId === null ||
                projectToMove?.folderId === undefined
              }
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-purple-500/50 hover:bg-muted/50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FolderOpen
                size={18}
                className="text-muted-foreground shrink-0"
              />
              <span className="text-sm font-medium text-foreground">
                Root (no folder)
              </span>
              {(projectToMove?.folderId === null ||
                projectToMove?.folderId === undefined) && (
                <span className="ml-auto text-xs text-muted-foreground">
                  Current
                </span>
              )}
            </button>

            {/* Folder options */}
            {folders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => handleMoveProject(folder.id)}
                disabled={
                  movingProject || projectToMove?.folderId === folder.id
                }
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-purple-500/50 hover:bg-muted/50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Folder size={18} className="text-purple-500 shrink-0" />
                <span className="text-sm font-medium text-foreground truncate">
                  {folder.name}
                </span>
                {projectToMove?.folderId === folder.id && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    Current
                  </span>
                )}
              </button>
            ))}

            {folders.length === 0 && (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">
                  No folders yet. Create a folder first.
                </p>
              </div>
            )}

            {movingProject && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
}
